import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';
import { notifyMany } from '../notifications/notificationService';

async function getTicketNotifContext(ticketId: string): Promise<{
  assigned_to: string | null;
  referent_id: string | null;
  sender_name: string | null;
} | null> {
  const { data } = await supabase
    .from('tickets')
    .select('assigned_to, referent_id, sender_name')
    .eq('id', ticketId)
    .single();
  return data ?? null;
}

export type Ticket = Tables<'tickets'>;
export type TicketActivityLog = Tables<'ticket_activity_log'>;
export type TicketNote = Tables<'ticket_notes'>;

export interface TicketNoteWithAuthor extends TicketNote {
  author: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
}

export interface TicketFilters {
  status?: string[];
  priority?: string[];
  category_ids?: string[];
  assigned_to?: string | null | 'me' | 'unassigned';
  referent_of_me?: boolean;
  search?: string;
  date_from?: string;
  date_to?: string;
  include_archived?: boolean;
  archived_only?: boolean;
}

export interface TicketWithRelations extends Ticket {
  category: Tables<'ticket_categories'> | null;
  assignee: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'email' | 'avatar_url'> | null;
  referent: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
}

export interface TicketActivityWithActor extends TicketActivityLog {
  actor: Pick<Tables<'collaborators'>, 'full_name' | 'avatar_url'> | null;
}

// Onglets de la vue Demandes — helpers purs dans ./ticketTabs (sans dépendance
// Supabase, donc testables), ré-exportés ici pour rester l'import unique.
export {
  classifyTicketTab,
  TICKET_TABS,
  TICKET_TAB_LABELS,
  RECRUITMENT_CATEGORY_SLUG,
  type TicketTab,
} from './ticketTabs';

export interface TicketCounts {
  new: number;
  awaiting_claim: number;
  in_progress: number;
  closed: number;
  unseen_mine: number;
  total_open: number;
}

// ─── Queries ────────────────────────────────────────────────────────────────

export async function getTickets(filters: TicketFilters): Promise<TicketWithRelations[]> {
  let query = supabase
    .from('tickets')
    .select(`
      *,
      category:ticket_categories(*),
      assignee:collaborators!tickets_assigned_to_fkey(id, full_name, email, avatar_url),
      referent:collaborators!tickets_referent_id_fkey(id, full_name, avatar_url)
    `)
    .order('created_at', { ascending: false });

  if (filters.archived_only) {
    query = query.eq('is_archived', true);
  } else if (!filters.include_archived) {
    query = query.eq('is_archived', false);
  }

  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters.priority && filters.priority.length > 0) {
    query = query.in('priority', filters.priority);
  }

  if (filters.category_ids && filters.category_ids.length > 0) {
    query = query.in('category_id', filters.category_ids);
  }

  if (filters.assigned_to === 'unassigned') {
    query = query.is('assigned_to', null);
  } else if (filters.assigned_to && filters.assigned_to !== 'me') {
    query = query.eq('assigned_to', filters.assigned_to);
  }
  // 'me' is handled client-side after auth check

  if (filters.date_from) {
    query = query.gte('created_at', filters.date_from);
  }

  if (filters.date_to) {
    query = query.lte('created_at', filters.date_to);
  }

  const { data, error } = await query;
  if (error) throw error;

  let results = (data ?? []) as TicketWithRelations[];

  if (filters.search) {
    const term = filters.search.toLowerCase();
    results = results.filter(
      (t) =>
        (t.sender_name ?? '').toLowerCase().includes(term) ||
        (t.sender_email ?? '').toLowerCase().includes(term) ||
        t.message.toLowerCase().includes(term),
    );
  }

  return results;
}

export async function getTicketById(id: string): Promise<TicketWithRelations | null> {
  const { data, error } = await supabase
    .from('tickets')
    .select(`
      *,
      category:ticket_categories(*),
      assignee:collaborators!tickets_assigned_to_fkey(id, full_name, email, avatar_url),
      referent:collaborators!tickets_referent_id_fkey(id, full_name, avatar_url)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as TicketWithRelations;
}

export async function getTicketActivityLog(ticketId: string): Promise<TicketActivityWithActor[]> {
  const { data, error } = await supabase
    .from('ticket_activity_log')
    .select(`
      *,
      actor:collaborators!ticket_activity_log_actor_id_fkey(full_name, avatar_url)
    `)
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data ?? []) as TicketActivityWithActor[];
}

export async function getTicketCounts(currentCollaboratorId?: string): Promise<TicketCounts> {
  const { data, error } = await supabase
    .from('tickets')
    .select('status, assigned_to, first_seen_at')
    .eq('is_archived', false);

  if (error) throw error;

  const rows = data ?? [];

  const newCount = rows.filter((r) => r.status === 'new').length;
  const inProgressCount = rows.filter((r) => r.status === 'in_progress').length;
  const closedCount = rows.filter((r) => r.status === 'closed').length;
  const totalOpen = newCount + inProgressCount;

  // Tickets assignés à qqn mais pas encore pris en charge (status='new' AND assigned_to set).
  // Filtré sur le collab courant si fourni, sinon global cabinet.
  const awaitingClaim = rows.filter(
    (r) =>
      r.status === 'new' &&
      r.assigned_to !== null &&
      (!currentCollaboratorId || r.assigned_to === currentCollaboratorId),
  ).length;

  const unseenMine = currentCollaboratorId
    ? rows.filter(
        (r) =>
          r.assigned_to === currentCollaboratorId &&
          r.first_seen_at === null &&
          r.status !== 'closed' &&
          r.status !== 'spam',
      ).length
    : 0;

  return {
    new: newCount,
    awaiting_claim: awaitingClaim,
    in_progress: inProgressCount,
    closed: closedCount,
    unseen_mine: unseenMine,
    total_open: totalOpen,
  };
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export async function getTicketNotes(ticketId: string): Promise<TicketNoteWithAuthor[]> {
  const { data, error } = await supabase
    .from('ticket_notes')
    .select('*, author:collaborators!ticket_notes_author_id_fkey(id, full_name, avatar_url)')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []) as TicketNoteWithAuthor[];
}

export async function createTicketNote(
  ticketId: string,
  content: string,
  authorId: string,
): Promise<void> {
  const { error: insertErr } = await supabase.from('ticket_notes').insert({
    ticket_id: ticketId,
    author_id: authorId,
    content,
  });
  if (insertErr) throw insertErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'note_added',
    actor_id: authorId,
    actor_type: 'collaborator',
    details: {},
  });
  if (logErr) throw logErr;

  const ctx = await getTicketNotifContext(ticketId);
  if (ctx) {
    const preview = content.slice(0, 140);
    const title = `Nouvelle note sur le ticket de ${ctx.sender_name ?? 'un expéditeur'}`;
    const recipients = [ctx.assigned_to, ctx.referent_id].filter(
      (id): id is string => !!id && id !== authorId,
    );
    void notifyMany(
      recipients.map((id) => ({
        recipient_id: id,
        type: 'ticket_note_added',
        title,
        message: preview,
        metadata: { ticket_id: ticketId, actor_id: authorId, note_preview: preview },
      })),
    );
  }
}

export async function assignTicket(
  ticketId: string,
  collaboratorId: string,
  assignedByCollaboratorId: string,
  message?: string,
): Promise<void> {
  // Détecter si c'est une réassignation (ticket déjà assigné à quelqu'un d'autre).
  const { data: existing } = await supabase
    .from('tickets')
    .select('assigned_to')
    .eq('id', ticketId)
    .single();
  const isReassignment = Boolean(existing?.assigned_to && existing.assigned_to !== collaboratorId);
  const notifReason = isReassignment ? 'reassigned' : 'manual_assigned';

  // Assignation : on pose assigned_to / assigned_at / assigned_by.
  // Le statut reste inchangé — un ticket 'new' reste 'new' tant que le
  // collaborateur ne l'a pas pris en charge via claimTicket().
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({
      assigned_to: collaboratorId,
      assigned_at: new Date().toISOString(),
      assigned_by: assignedByCollaboratorId,
      is_auto_assigned: false,
    })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'assigned',
    actor_id: assignedByCollaboratorId,
    actor_type: 'collaborator',
    details: { to: collaboratorId },
  });

  if (logErr) throw logErr;

  // In-app notification — via RPC directement pour éviter l'aléa JWT de l'edge function.
  const ctx = await getTicketNotifContext(ticketId);
  if (ctx) {
    void notifyMany([{
      recipient_id: collaboratorId,
      type: 'ticket_assigned',
      title: `${isReassignment ? 'Ticket réassigné' : 'Nouveau ticket'} : ${ctx.sender_name ?? 'Anonyme'}`,
      message: isReassignment ? 'Un ticket vous a été réassigné.' : 'Un ticket vous a été assigné manuellement.',
      metadata: { ticket_id: ticketId, reason: notifReason },
    }]);
  }

  // Fire-and-forget: email via edge function.
  // Le token est passé explicitement — avec le format de clé sb_publishable_*, functions.invoke()
  // peut envoyer la publishable key (non-JWT) comme fallback, ce qui provoque un 401 silencieux.
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!session) return;
    supabase.functions.invoke('notify-ticket-assignee', {
      body: { ticket_id: ticketId, reason: notifReason, message: message ?? null },
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch((err) => {
      console.error('[assignTicket] notify-ticket-assignee invoke failed:', err);
    });
  });
}

export async function claimTicket(
  ticketId: string,
  byCollaboratorId: string,
): Promise<void> {
  // "Prendre en charge" : le collaborateur assigné passe le ticket en 'in_progress'.
  // RLS empêche déjà un collab non-assigné de le faire (sauf admin/secretary).
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({ status: 'in_progress' })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'claimed',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: { from: 'new', to: 'in_progress' },
  });

  if (logErr) throw logErr;
}

export async function unassignTicket(ticketId: string, byCollaboratorId: string): Promise<void> {
  const ctxBefore = await getTicketNotifContext(ticketId);
  const previousAssignee = ctxBefore?.assigned_to ?? null;

  const { error: updateErr } = await supabase
    .from('tickets')
    .update({
      assigned_to: null,
      assigned_at: null,
      assigned_by: null,
      status: 'new',
      is_auto_assigned: false,
    })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'unassigned',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: {},
  });

  if (logErr) throw logErr;

  if (previousAssignee && previousAssignee !== byCollaboratorId) {
    void notifyMany([
      {
        recipient_id: previousAssignee,
        type: 'ticket_unassigned',
        title: `Ticket retiré : ${ctxBefore?.sender_name ?? 'expéditeur'}`,
        message: 'Ce ticket ne vous est plus assigné.',
        metadata: { ticket_id: ticketId, actor_id: byCollaboratorId },
      },
    ]);
  }
}

export async function updateTicketStatus(
  ticketId: string,
  status: string,
  byCollaboratorId: string,
): Promise<void> {
  const { data: current, error: fetchErr } = await supabase
    .from('tickets')
    .select('status')
    .eq('id', ticketId)
    .single();

  if (fetchErr) throw fetchErr;

  let updateErr;
  if (status === 'closed') {
    ({ error: updateErr } = await supabase
      .from('tickets')
      .update({ status, closed_at: new Date().toISOString(), closed_by: byCollaboratorId })
      .eq('id', ticketId));
  } else {
    ({ error: updateErr } = await supabase
      .from('tickets')
      .update({ status })
      .eq('id', ticketId));
  }

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'status_changed',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: { from: current.status, to: status },
  });

  if (logErr) throw logErr;

  const ctx = await getTicketNotifContext(ticketId);
  if (ctx) {
    const recipients = [ctx.assigned_to, ctx.referent_id].filter(
      (id): id is string => !!id && id !== byCollaboratorId,
    );
    void notifyMany(
      recipients.map((id) => ({
        recipient_id: id,
        type: 'ticket_status_changed',
        title: `Statut modifié : ${ctx.sender_name ?? 'ticket'}`,
        message: `Passé de « ${current.status} » à « ${status} ».`,
        metadata: {
          ticket_id: ticketId,
          actor_id: byCollaboratorId,
          from_status: current.status,
          to_status: status,
        },
      })),
    );
  }
}

export async function updateTicketPriority(
  ticketId: string,
  priority: string,
  byCollaboratorId: string,
): Promise<void> {
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({ priority })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'priority_changed',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: { priority },
  });

  if (logErr) throw logErr;
}

export async function markTicketAsSeen(ticketId: string): Promise<void> {
  // Only update if not already seen
  const { data, error: fetchErr } = await supabase
    .from('tickets')
    .select('first_seen_at')
    .eq('id', ticketId)
    .single();

  if (fetchErr) return; // Silently fail — non-critical

  if (data?.first_seen_at !== null) return;

  await supabase
    .from('tickets')
    .update({ first_seen_at: new Date().toISOString() })
    .eq('id', ticketId);
}

export async function deleteTicket(ticketId: string): Promise<void> {
  const { error } = await supabase.from('tickets').delete().eq('id', ticketId);
  if (error) throw error;
}

export async function archiveTicket(ticketId: string, byCollaboratorId: string): Promise<void> {
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({ is_archived: true })
    .eq('id', ticketId);
  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'archived',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: {},
  });
  if (logErr) throw logErr;
}

export async function updateTicketCategoryOnTicket(
  ticketId: string,
  categoryId: string | null,
  byCollaboratorId: string,
): Promise<void> {
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({ category_id: categoryId })
    .eq('id', ticketId);
  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'category_changed',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: { category_id: categoryId },
  });
  if (logErr) throw logErr;
}

export async function updateTicketClientFields(
  ticketId: string,
  fields: { client_status?: string | null },
  byCollaboratorId: string,
): Promise<void> {
  const { error: updateErr } = await supabase
    .from('tickets')
    .update(fields)
    .eq('id', ticketId);
  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'client_fields_updated',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: fields,
  });
  if (logErr) throw logErr;
}

// ─── Attachments ────────────────────────────────────────────────────────────

export interface TicketAttachment extends Tables<'ticket_attachments'> {
  uploader: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
  signed_url?: string;
}

const ATTACHMENT_BUCKET = 'ticket-attachments';
const SIGNED_URL_TTL = 3600; // 1 hour

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export async function getTicketAttachments(ticketId: string): Promise<TicketAttachment[]> {
  const { data, error } = await supabase
    .from('ticket_attachments')
    .select('*, uploader:collaborators!ticket_attachments_uploaded_by_fkey(id, full_name, avatar_url)')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  const rows = (data ?? []) as TicketAttachment[];

  if (rows.length === 0) return rows;

  // Batch-generate signed URLs
  const { data: signedData } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .createSignedUrls(rows.map((r) => r.storage_path), SIGNED_URL_TTL);

  const urlMap = new Map(
    (signedData ?? []).map((s) => [s.path, s.signedUrl]),
  );

  return rows.map((r) => ({
    ...r,
    signed_url: urlMap.get(r.storage_path) ?? undefined,
  }));
}

export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
  uploadedById: string,
): Promise<void> {
  const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
  const safeName = sanitizeFilename(file.name);
  const uniqueId = crypto.randomUUID();
  const storagePath = `tickets/${ticketId}/${uniqueId}${ext ? `_${safeName}` : safeName}`;

  const { error: storageErr } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .upload(storagePath, file, { contentType: file.type, upsert: false });

  if (storageErr) {
    console.error('[uploadTicketAttachment] storage upload failed:', storageErr);
    throw storageErr;
  }

  const { error: dbErr } = await supabase.from('ticket_attachments').insert({
    ticket_id: ticketId,
    uploaded_by: uploadedById,
    storage_path: storagePath,
    filename: file.name,
    mime_type: file.type,
    size_bytes: file.size,
  });

  if (dbErr) {
    console.error('[uploadTicketAttachment] DB insert failed, rolling back storage:', dbErr);
    await supabase.storage.from(ATTACHMENT_BUCKET).remove([storagePath]);
    throw dbErr;
  }

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'attachment_added',
    actor_id: uploadedById,
    actor_type: 'collaborator',
    details: { filename: file.name, size_bytes: file.size },
  });
  if (logErr) throw logErr;
}

export async function deleteTicketAttachment(
  attachmentId: string,
  storagePath: string,
  ticketId: string,
  byCollaboratorId: string,
): Promise<void> {
  const { error: dbErr } = await supabase
    .from('ticket_attachments')
    .delete()
    .eq('id', attachmentId);

  if (dbErr) throw dbErr;

  const { error: storageErr } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .remove([storagePath]);

  if (storageErr) throw storageErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'attachment_deleted',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: {},
  });
  if (logErr) throw logErr;
}

// ─── Délégation ─────────────────────────────────────────────────────────────

export async function delegateTicket(
  ticketId: string,
  delegateToId: string,
  byCollaboratorId: string,
): Promise<void> {
  // Le délégant devient référent ; le ticket est confié au délégataire.
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({
      referent_id: byCollaboratorId,
      assigned_to: delegateToId,
      assigned_at: new Date().toISOString(),
      assigned_by: byCollaboratorId,
      is_auto_assigned: false,
    })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'delegated',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: { to: delegateToId, referent: byCollaboratorId },
  });

  if (logErr) throw logErr;

  // Fire-and-forget notification to the delegate.
  // JWT passé explicitement — même raison que assignTicket() : éviter le 401 silencieux.
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!session) return;
    supabase.functions.invoke('notify-ticket-assignee', {
      body: { ticket_id: ticketId, reason: 'delegated' },
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch((err) => {
      console.error('[delegateTicket] notify-ticket-assignee invoke failed:', err);
    });
  });
}

// ─── Référent ────────────────────────────────────────────────────────────────

export async function setTicketReferent(
  ticketId: string,
  referentId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_ticket_referent', {
    p_ticket_id: ticketId,
    // p_referent_id NULL = désassigner le référent (la fonction SQL l'accepte),
    // mais le générateur de types Supabase l'infère non-null. Cast volontaire.
    p_referent_id: referentId as string,
  });
  if (error) throw error;
}

export async function addTicketResponseNote(
  ticketId: string,
  note: string,
  byCollaboratorId: string,
): Promise<void> {
  const { error: updateErr } = await supabase
    .from('tickets')
    .update({
      response_note: note,
      responded_at: new Date().toISOString(),
    })
    .eq('id', ticketId);

  if (updateErr) throw updateErr;

  const { error: logErr } = await supabase.from('ticket_activity_log').insert({
    ticket_id: ticketId,
    action: 'note_added',
    actor_id: byCollaboratorId,
    actor_type: 'collaborator',
    details: {},
  });

  if (logErr) throw logErr;
}

// ─── Réponse au candidat (recrutement) ───────────────────────────────────────

export async function getTicketEmails(ticketId: string): Promise<Tables<'ticket_emails'>[]> {
  const { data, error } = await supabase
    .from('ticket_emails')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('sent_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Envoie une réponse au candidat via l'edge function send-ticket-reply.
 *
 * ATTENTION — contrairement aux notifications internes de ce fichier (fire-and-forget),
 * cet appel est AWAITÉ et THROW en cas d'échec : l'utilisateur doit savoir si le mail
 * est parti ou non (un email sortant est irréversible).
 *
 * Le JWT est passé explicitement : avec le format de clé sb_publishable_*,
 * functions.invoke() peut envoyer la publishable key (non-JWT) → 401 silencieux.
 */
export async function sendTicketReply(params: {
  ticketId: string;
  templateKey: string | null;
  subject: string;
  body: string;
}): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expirée — reconnectez-vous.');

  const { data, error } = await supabase.functions.invoke('send-ticket-reply', {
    body: {
      ticket_id: params.ticketId,
      template_key: params.templateKey,
      subject: params.subject,
      body: params.body,
    },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (error) {
    // L'edge function renvoie { error: "..." } avec un status non-2xx →
    // supabase-js expose le corps dans error.context. On tente d'en extraire le message.
    let message = "L'envoi a échoué.";
    try {
      const ctx = await (error as unknown as { context?: Response }).context?.json();
      if (ctx?.error) message = String(ctx.error);
    } catch {
      // corps illisible → on garde le message générique
    }
    throw new Error(message);
  }

  if (data && (data as { error?: string }).error) {
    throw new Error(String((data as { error: string }).error));
  }
}
